import React from 'react';
import { Building, Briefcase, Hotel, ArrowRight } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

const iconMap = {
  'Building': Building,
  'Briefcase': Briefcase,
  'Hotel': Hotel
};

export const Solutions = () => {
  const { t } = useTranslation();
  
  // Get solutions dynamically based on current language
  const solutions = [
    { id: 1, category: t('solutions.item1.category'), title: t('solutions.item1.title'), description: t('solutions.item1.description'), features: [t('solutions.item1.feature1'), t('solutions.item1.feature2'), t('solutions.item1.feature3')], icon: "Building" },
    { id: 2, category: t('solutions.item2.category'), title: t('solutions.item2.title'), description: t('solutions.item2.description'), features: [t('solutions.item2.feature1'), t('solutions.item2.feature2'), t('solutions.item2.feature3')], icon: "Briefcase" },
    { id: 3, category: t('solutions.item3.category'), title: t('solutions.item3.title'), description: t('solutions.item3.description'), features: [t('solutions.item3.feature1'), t('solutions.item3.feature2'), t('solutions.item3.feature3')], icon: "Hotel" }
  ];
  
  return (
    <section id="solutions" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#C5A35C]/10 border border-[#C5A35C]/20 mb-6">
            <span className="text-[#C5A35C] text-sm font-semibold">{t('solutions.badge')}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t('solutions.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('solutions.description')}
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {solutions.map((solution, index) => {
            const Icon = iconMap[solution.icon];
            return (
              <Card 
                key={solution.id}
                className="group overflow-hidden border-2 border-gray-100 hover:border-[#C5A35C]/30 transition-all duration-300 hover:shadow-2xl"
              >
                {/* Category Badge */}
                <div className="bg-gradient-to-r from-[#C5A35C] to-[#B8933D] text-white px-6 py-3">
                  <span className="text-sm font-semibold uppercase tracking-wide">
                    {solution.category}
                  </span>
                </div>

                <div className="p-8">
                  {/* Icon */}
                  <div className="mb-6 inline-flex p-4 rounded-2xl bg-gradient-to-br from-[#C5A35C]/10 to-[#C5A35C]/5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-10 h-10 text-[#C5A35C]" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {solution.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {solution.features.map((feature) => (
                      <li key={`${solution.id}-${feature}`} className="flex items-center gap-3 text-sm text-gray-700">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#C5A35C]/10 flex items-center justify-center">
                          <span className="text-[#C5A35C] text-xs">✓</span>
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    variant="outline"
                    className="w-full group-hover:bg-[#C5A35C] group-hover:text-white group-hover:border-[#C5A35C] transition-all duration-300"
                  >
                    {t('solutions.cta')}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
